import type { TCreditTransactionKind } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Badge, Box, Center, Divider, Group, Pagination, Paper, Select, Stack, Table, Text } from "@mantine/core"
import {
  IconAdjustments,
  IconArrowBackUp,
  IconCalendar,
  IconGift,
  IconHourglassEmpty,
  IconShoppingCart,
} from "@tabler/icons-react"
import { useCallback, useMemo, useState } from "react"
import { formatDate, formatInteger } from "../../../../lib/format-date"
import { kindColor, mergeCreditActivity, type TActivityEntry } from "../credits-helpers"
import { creditMeetingTitle } from "../meeting-label"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

/** The ledger kinds, in the order they appear in the filter dropdown. */
const KINDS: readonly TCreditTransactionKind[] = ["grant", "purchase", "expiry", "adjustment", "refund"]

type TKindFilter = TCreditTransactionKind | "usage" | "all"

function isKindFilter(value: string | null): value is TKindFilter {
  switch (value) {
    case "all":
    case "usage":
    case "grant":
    case "purchase":
    case "expiry":
    case "adjustment":
    case "refund":
      return true
    default:
      return false
  }
}

type TActivityKind = TCreditTransactionKind | "usage"

function kindLabel(i18n: ReturnType<typeof useLingui>["i18n"], kind: TActivityKind): string {
  switch (kind) {
    case "grant":
      return i18n._(msg`Monthly grant`)
    case "purchase":
      return i18n._(msg`Top-up`)
    case "expiry":
      return i18n._(msg`Expired`)
    case "adjustment":
      return i18n._(msg`Adjustment`)
    case "refund":
      return i18n._(msg`Refund`)
    case "usage":
      return i18n._(msg`Meeting`)
  }
}

function kindBadgeColor(kind: TActivityKind): "success" | "brand" | "warning" | "info" {
  if (kind === "usage") return "info"
  return kindColor(kind)
}

function KindIcon({ kind }: { kind: TActivityKind }): React.JSX.Element {
  switch (kind) {
    case "grant":
      return <IconGift size={12} aria-hidden />
    case "purchase":
      return <IconShoppingCart size={12} aria-hidden />
    case "expiry":
      return <IconHourglassEmpty size={12} aria-hidden />
    case "adjustment":
      return <IconAdjustments size={12} aria-hidden />
    case "refund":
      return <IconArrowBackUp size={12} aria-hidden />
    case "usage":
      return <IconCalendar size={12} aria-hidden />
  }
}

/** Signed credit amount — inflows read as success/green, outflows stay muted. */
function Amount({ credits }: { credits: number }): React.JSX.Element {
  const { i18n } = useLingui()
  return (
    <Text size="sm" fw={600} className={classes.amount} data-sign={credits >= 0 ? "in" : "out"}>
      {credits >= 0 ? "+" : "−"}
      {formatInteger(i18n, Math.abs(credits))}
    </Text>
  )
}

/** What one activity row shows. Usage spends credits, so its amount is negative. */
interface IActivityLine {
  key: string
  kind: TCreditTransactionKind | "usage"
  detail: string
  at: string
  expiresAt: string | null
  credits: number
}

function toLine(i18n: ReturnType<typeof useLingui>["i18n"], entry: TActivityEntry): IActivityLine {
  if (entry.kind === "usage") {
    return {
      key: `usage-${entry.usage.meetingId.length > 0 ? entry.usage.meetingId : "unattributed"}`,
      kind: "usage",
      detail: creditMeetingTitle(i18n, entry.usage),
      at: entry.at,
      expiresAt: null,
      credits: -Math.abs(entry.usage.credits),
    }
  }
  return {
    key: `ledger-${entry.transaction.id}`,
    kind: entry.transaction.kind,
    detail: entry.transaction.source,
    at: entry.at,
    expiresAt: entry.transaction.expiresAt,
    credits: entry.transaction.credits,
  }
}

/**
 * Activity — this period's credit use per meeting, merged with grants, top-ups,
 * expiries and adjustments, newest first. The list is empty only when both
 * sides are empty. Desktop renders a table, phones a stacked card per row.
 * CREDITS ONLY — the amount is a count, never money.
 */
export const CreditLedgerView = ({
  ledgerRows,
  usageRows,
  page,
  pageSize,
  isLoading = false,
  onPageChange,
}: IProps): React.JSX.Element => {
  const [kindFilter, setKindFilter] = useState<TKindFilter>("all")
  const { i18n } = useLingui()

  const merged = useMemo(() => mergeCreditActivity(ledgerRows, usageRows), [ledgerRows, usageRows])
  const filtered = useMemo(() => {
    if (kindFilter === "all") return merged
    if (kindFilter === "usage") return merged.filter((entry) => entry.kind === "usage")
    return merged.filter((entry) => entry.kind === "ledger" && entry.transaction.kind === kindFilter)
  }, [merged, kindFilter])

  const totalPages = Math.max(1, Math.ceil(filtered.length / Math.max(1, pageSize)))
  const safePage = Math.min(Math.max(1, page), totalPages)
  const visibleRows = useMemo(() => {
    const start = (safePage - 1) * pageSize
    return filtered.slice(start, start + pageSize).map((entry) => toLine(i18n, entry))
  }, [filtered, i18n, pageSize, safePage])

  const filterData = useMemo(
    () => [
      { value: "all", label: i18n._(msg`All types`) },
      { value: "usage", label: i18n._(msg`Meetings`) },
      ...KINDS.map((kind) => ({ value: kind, label: kindLabel(i18n, kind) })),
    ],
    [i18n],
  )

  const handleFilter = useCallback(
    (value: string | null): void => {
      setKindFilter(isKindFilter(value) ? value : "all")
      onPageChange(1)
    },
    [onPageChange],
  )

  const pager =
    totalPages > 1 ? (
      <Group justify="center" mt="md">
        <Pagination
          total={totalPages}
          value={safePage}
          onChange={onPageChange}
          size="sm"
          disabled={isLoading}
          aria-label={i18n._(msg`Ledger pages`)}
        />
      </Group>
    ) : null

  return (
    <Paper withBorder radius="md" p="lg" data-loading={isLoading || undefined} className={classes.wrap}>
      <Group justify="space-between" align="center" mb="md" wrap="nowrap">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          <Trans>Activity</Trans>
        </Text>
        {merged.length > 0 ? (
          <Select
            value={kindFilter}
            onChange={handleFilter}
            data={filterData}
            size="xs"
            w={150}
            allowDeselect={false}
            aria-label={i18n._(msg`Filter activity by type`)}
          />
        ) : null}
      </Group>

      {merged.length === 0 ? (
        isLoading ? null : (
          <Center py="xl">
            <Text c="dimmed" size="sm">
              <Trans>No credit activity yet</Trans>
            </Text>
          </Center>
        )
      ) : visibleRows.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed" size="sm">
            <Trans>No matching activity</Trans>
          </Text>
        </Center>
      ) : (
        <>
          {/* Desktop table */}
          <Box visibleFrom="sm">
            <Table striped highlightOnHover verticalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Trans>Type</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Source</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Date</Trans>
                  </Table.Th>
                  <Table.Th>
                    <Trans>Expires</Trans>
                  </Table.Th>
                  <Table.Th ta="right">
                    <Trans>Credits</Trans>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {visibleRows.map((row) => (
                  <Table.Tr key={row.key}>
                    <Table.Td>
                      <Badge
                        variant="light"
                        color={kindBadgeColor(row.kind)}
                        size="sm"
                        leftSection={<KindIcon kind={row.kind} />}
                      >
                        {kindLabel(i18n, row.kind)}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed" truncate>
                        {row.detail}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{row.at ? formatDate(i18n, row.at) : "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {row.expiresAt ? formatDate(i18n, row.expiresAt) : "—"}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Amount credits={row.credits} />
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>

          {/* Mobile stacked cards */}
          <Box hiddenFrom="sm">
            <Stack gap="sm">
              {visibleRows.map((row) => (
                <Paper key={row.key} withBorder p="md" radius="sm">
                  <Group justify="space-between" wrap="nowrap">
                    <Badge
                      variant="light"
                      color={kindBadgeColor(row.kind)}
                      size="sm"
                      leftSection={<KindIcon kind={row.kind} />}
                    >
                      {kindLabel(i18n, row.kind)}
                    </Badge>
                    <Amount credits={row.credits} />
                  </Group>
                  <Divider my="xs" />
                  <Group justify="space-between" wrap="nowrap">
                    <Text size="xs" c="dimmed" truncate>
                      {row.detail}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {row.at ? formatDate(i18n, row.at) : "—"}
                    </Text>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Box>

          {pager}
        </>
      )}
    </Paper>
  )
}
